/**
 * Restoration · mic record — quick MediaRecorder wrapper.
 *
 * Use case: user is digitising vinyl through their PC line-in, or just
 * recording from a USB turntable / dictaphone via microphone. Going
 * through the OS file picker after Audacity-ing is friction; recording
 * directly into the restoration tool is one click.
 *
 * Output: a Blob ready to feed into the same `tool-uploader:decoded`
 * flow the regular file picker uses. The blade wires the recording
 * blob through `ToolUploader.inject()` so the rest of the pipeline
 * (decode, diagnose, workspace activation) runs unchanged.
 *
 * Format priority:
 *   1. audio/webm;codecs=opus  (Chrome, Edge, Firefox) — most compact
 *   2. audio/mp4               (Safari)
 *   3. audio/webm              (older Chrome)
 *
 * Caveat: MediaRecorder writes opus/aac frames; our AudioDecoderRobust.robust
 * already handles both. iOS Safari only supports mp4 here. No raw PCM
 * recording for now — codec-encoded blob is fine because the decoder
 * brings it back to a Float32 AudioBuffer anyway.
 */
(function () {
    'use strict';

    function pickMime() {
        if (typeof MediaRecorder === 'undefined') return null;
        const candidates = [
            'audio/webm;codecs=opus',
            'audio/mp4',
            'audio/webm',
            'audio/ogg;codecs=opus',
        ];
        for (const m of candidates) {
            if (MediaRecorder.isTypeSupported(m)) return m;
        }
        return null;
    }

    class MicRecorder {
        constructor() {
            this.stream = null;
            this.recorder = null;
            this.chunks = [];
            this.mime = pickMime();
            this.startedAt = 0;
            this._tick = null;
            this.onTick = function () {}; // (elapsedSec) =>
            this.onStop = function () {}; // (blob, durationSec, mime) =>
            this.onError = function () {}; // (err) =>
        }
        isSupported() {
            return !!(this.mime && navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
        }
        async start() {
            if (!this.isSupported()) {
                throw new Error('Recording not supported in this browser');
            }
            try {
                this.stream = await window.__tmbGum({
                    audio: {
                        echoCancellation: false, // we WANT the raw input for restoration use
                        noiseSuppression: false,
                        autoGainControl: false,
                        channelCount: 2,
                        sampleRate: 48000,
                    },
                });
            } catch (e) {
                throw new Error('Microphone access denied or unavailable: ' + (e && e.message || e));
            }
            this.recorder = new MediaRecorder(this.stream, { mimeType: this.mime });
            this.chunks = [];
            this.recorder.ondataavailable = (e) => {
                if (e.data && e.data.size) this.chunks.push(e.data);
            };
            this.recorder.onstop = () => {
                const blob = new Blob(this.chunks, { type: this.mime });
                const dur = (performance.now() - this.startedAt) / 1000;
                this._releaseStream();
                if (this._tick) { clearInterval(this._tick); this._tick = null; }
                this.onStop(blob, dur, this.mime);
            };
            this.recorder.onerror = (e) => { this.onError(e.error || e); };
            this.recorder.start(250); // chunk size for streaming use
            this.startedAt = performance.now();
            this._tick = setInterval(() => {
                this.onTick((performance.now() - this.startedAt) / 1000);
            }, 250);
        }
        stop() {
            if (this.recorder && this.recorder.state !== 'inactive') {
                this.recorder.stop();
            } else {
                this._releaseStream();
                if (this._tick) { clearInterval(this._tick); this._tick = null; }
            }
        }
        _releaseStream() {
            if (this.stream) {
                this.stream.getTracks().forEach(t => { try { t.stop(); } catch (_) {} });
                this.stream = null;
            }
        }
        cancel() {
            this.chunks = [];
            this.stop();
        }
    }

    window.RestorationMic = {
        isSupported: function () { return pickMime() !== null && navigator.mediaDevices && navigator.mediaDevices.getUserMedia; },
        create: function () { return new MicRecorder(); },
    };
})();
